import { useMemo, useState } from 'react';
import {
  Table,
  ActionIcon,
  Text,
  Menu,
  rem,
  TextInput,
  Paper,
  Stack,
  Group,
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash, IconSearch } from '@tabler/icons-react';
import type { Department } from '@/types/models';

interface DepartmentsTableProps {
  departments: Department[];
  onEdit: (department: Department) => void;
  onDelete: (department: Department) => void;
  isLoading?: boolean;
}

export function DepartmentsTable({
  departments,
  onEdit,
  onDelete,
  isLoading,
}: DepartmentsTableProps) {
  const [search, setSearch] = useState('');

  const filteredDepartments = useMemo(() => {
    return departments.filter((department) => {
      const matchesSearch =
        search === '' || department.name.toLowerCase().includes(search.toLowerCase());
      return matchesSearch;
    });
  }, [departments, search]);

  const rows = filteredDepartments.map((department) => (
    <Table.Tr key={department.id}>
      <Table.Td>
        <Text fw={500}>{department.name}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onEdit(department)}
            title="Edit"
          >
            <IconEdit size={16} />
          </ActionIcon>
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                color="red"
                leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => onDelete(department)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <TextInput
          placeholder="Search departments..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ maxWidth: 400 }}
        />
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={2}>
                    <Text ta="center" py="xl" c="dimmed">
                      Loading departments...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={2}>
                    <Text ta="center" py="xl" c="dimmed">
                      No departments found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Text size="sm" c="dimmed">
        Showing {filteredDepartments.length} of {departments.length} departments
      </Text>
    </Stack>
  );
}
